import { create } from 'zustand';
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { logger } from '@/lib/logger';

// Enable Immer to work with Sets and Maps
enableMapSet();

export type ViewMode = 'fixtures' | 'circuits' | 'panels' | 'controls' | 'rooms';
export type DrawerType =
  | 'fixture'
  | 'circuit'
  | 'control'
  | 'panel'
  | 'room'
  | 'product'
  | 'selection'
  | 'settings'
  | 'ai'
  | 'presentation'
  | 'annotation'
  | 'proposalItems'
  | 'portalProducts'
  | 'cablePath'
  | 'cableJunction';
export type EntityType =
  | 'fixture'
  | 'circuit'
  | 'control'
  | 'panel'
  | 'room'
  | 'product'
  | 'annotation'
  | 'cablePath'
  | 'cableJunction';
export type ToolType = 'select' | 'draw' | 'measure' | 'pan' | 'annotate';
export type FilterType = 'showOnlyUnassigned' | 'showOnlyUnassignedControls';
export type AnnotationType =
  | 'line'
  | 'rectangle'
  | 'circle'
  | 'text'
  | 'image'
  | 'arrow'
  | 'measurement';

interface ViewportState {
  scale: number;
  positionX: number;
  positionY: number;
}

interface SelectionState {
  fixtures: Set<string>;
  circuits: Set<string>;
  controls: Set<string>;
  rooms: Set<string>;
  panels: Set<string>;
  products: Set<string>;
  annotations: Set<string>;
  cablePaths: Set<string>;
  cableJunctions: Set<string>;
}

interface RoomState {
  roomsVisible: boolean;
  roomOpacity: number;
  confidenceThreshold: number;
  showDimensions: boolean;
  showArea: boolean;
  wallsVisible: boolean;
}

interface ToolbarState {
  activeTool: ToolType;
  isAIMenuOpen: boolean;
  showOnlyUnassigned: boolean;
  showOnlyUnassignedControls: boolean;
  activeMode?:
    | 'default'
    | 'circuit'
    | 'control-placement'
    | 'room-creation'
    | 'product-placement'
    | 'annotation'
    | 'cable-path-drawing'
    | 'cable-junction-placement';
  isSpacePressed?: boolean;
}

interface UIState {
  activeDrawer: DrawerType | null;
  previousDrawer: DrawerType | null;
  viewMode: ViewMode;
  presentationMode: boolean;
  presentationControlId: string | null; // 🆕 Selected control in presentation mode
  presentationLVProductIds: string[]; // 🆕 Selected LV products in presentation mode
  isLoading: boolean;
  hoveredEntityId: string | null;
  hoveredEntityType: EntityType | null;
  showOnlyUnassigned: boolean;
  showOnlyUnassignedControls: boolean;
  settingsTab?: string;
  toolbar: ToolbarState;
  isEditingFloorPlanImage: boolean;
  draggedItem: any | null;
}

interface FloorPlanState {
  currentId: string | null;
  viewport: ViewportState;
  imageSize: { width: number; height: number };
  isDrawingMode: boolean;
  isMeasuringMode: boolean;
}

type ImageMetadata = Record<string, never>;

interface AnnotationState {
  activeAnnotationType: AnnotationType | null;
  isAnnotating: boolean;
  isEditingText: boolean;
  currentStyle: {
    color: string;
    strokeWidth: number;
    fontSize: number;
    fontFamily: string;
    opacity: number;
  };
  showAnnotations: boolean;
}

interface ElevationState {
  isWallSelectionMode: boolean;
  selectedWallRoomId: string | null;
  selectedWallIndex: number | null;
  activeElevationId: string | null;
  elevationViewport: ViewportState;
}

interface AlignmentState {
  snapEnabled: boolean;
  snapThreshold: number; // in pixels
  showGuides: boolean;
}

interface LoadLinesState {
  visible: boolean;
  thickness: number; // 1 = thin, 2 = medium, 3 = thick
}

interface CablePathDrawingState {
  isDrawing: boolean;
  currentPoints: Array<{ x: number; y: number }>;
  startJunctionId: string | null;
  snapToJunctionId: string | null;
  liveLength: number;
  // Context menu state for polyline drawing
  showContextMenu: boolean;
  contextMenuScreenPosition: { x: number; y: number } | null;
  lastClickedPoint: { x: number; y: number } | null;
  // Layer visibility
  visible: boolean;
}

interface AppState {
  // State sections
  ui: UIState;
  selection: SelectionState;
  floorPlan: FloorPlanState;
  aiTools: {
    confidenceThreshold: number;
    markerOpacity: number;
    isProcessing: boolean;
    roomConfidenceThreshold: number;
    roomOpacity: number;
    isProcessingRooms: boolean;
    detectionStride: number;
  };
  rooms: RoomState;
  products: {
    showLabels: boolean;
    iconStyle: 'minimalist' | 'high-contrast';
  };
  annotations: AnnotationState;
  elevations: ElevationState;
  alignment: AlignmentState;
  cablePaths: CablePathDrawingState;
  loadLines: LoadLinesState;
}

interface AppActions {
  // UI Actions
  setActiveDrawer: (drawer: DrawerType | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setPresentationMode: (enabled: boolean) => void;
  setPresentationControlId: (id: string | null) => void; // 🆕 Set selected control in presentation mode
  setPresentationLVProductIds: (ids: string[]) => void; // 🆕 Set selected LV products in presentation mode
  setLoading: (loading: boolean) => void;
  setHoveredEntity: (id: string | null, type: EntityType | null) => void;
  setShowOnlyUnassigned: (show: boolean) => void;
  setShowOnlyUnassignedControls: (show: boolean) => void;
  setSettingsTab: (tab: string) => void;
  setEditingFloorPlanImage: (editing: boolean) => void;
  setDraggedItem: (item: any | null) => void;

  // Toolbar Actions
  setActiveTool: (tool: ToolType) => void;
  toggleAIMenu: () => void;
  setFilter: (filter: FilterType, value: boolean) => void;
  resetToolbar: () => void;
  setActiveMode: (
    mode: 'default' | 'circuit' | 'control-placement' | 'room-creation' | 'product-placement',
  ) => void;
  setSpacePressed: (pressed: boolean) => void;

  // Selection Actions
  selectEntity: (type: EntityType, id: string) => void;
  toggleEntitySelection: (type: EntityType, id: string) => void;
  selectMultipleEntities: (type: EntityType, ids: string[]) => void;
  clearEntitySelection: (type: EntityType) => void;
  clearAllSelections: () => void;
  isEntitySelected: (type: EntityType, id: string) => boolean;

  // Floor Plan Actions
  setCurrentFloorPlan: (id: string | null) => void;
  updateViewport: (viewport: Partial<ViewportState>) => void;
  resetViewport: () => void;
  setImageSize: (size: { width: number; height: number }) => void;
  setDrawingMode: (enabled: boolean) => void;
  setMeasuringMode: (enabled: boolean) => void;

  // AI Tools Actions
  setConfidenceThreshold: (threshold: number) => void;
  setMarkerOpacity: (opacity: number) => void;
  setIsProcessing: (processing: boolean) => void;
  setRoomConfidenceThreshold: (threshold: number) => void;
  setRoomOpacity: (opacity: number) => void;
  setIsProcessingRooms: (processing: boolean) => void;
  setDetectionStride: (stride: number) => void;

  // Room Actions
  toggleRoomVisibility: () => void;
  setRoomVisibility: (visible: boolean) => void;
  setRoomDimensionsVisible: (visible: boolean) => void;
  setRoomAreaVisible: (visible: boolean) => void;
  toggleRoomDimensions: () => void;
  toggleWallVisibility: () => void;
  setWallVisibility: (visible: boolean) => void;

  // Product Actions
  setProductLabelsVisible: (visible: boolean) => void;
  setProductIconStyle: (style: 'minimalist' | 'high-contrast') => void;

  // Annotation Actions
  setActiveAnnotationType: (type: AnnotationType | null) => void;
  setIsAnnotating: (isAnnotating: boolean) => void;
  setIsEditingText: (isEditing: boolean) => void;
  updateAnnotationStyle: (style: Partial<AnnotationState['currentStyle']>) => void;
  toggleAnnotationVisibility: () => void;
  setAnnotationVisibility: (visible: boolean) => void;

  // Elevation Actions
  setWallSelectionMode: (enabled: boolean) => void;
  setSelectedWall: (roomId: string | null, wallIndex: number | null) => void;
  setActiveElevation: (elevationId: string | null) => void;
  updateElevationViewport: (viewport: Partial<ViewportState>) => void;
  resetElevationViewport: () => void;

  // Alignment Actions
  toggleSnapEnabled: () => void;
  setSnapThreshold: (threshold: number) => void;
  toggleShowGuides: () => void;

  // Cable Path Actions
  setCablePathDrawing: (state: Partial<CablePathDrawingState>) => void;
  startCablePathDrawing: (startJunctionId?: string | null) => void;
  addCablePathPoint: (point: { x: number; y: number }) => void;
  updateCablePathLiveLength: (length: number) => void;
  setSnapToJunction: (junctionId: string | null) => void;
  finishCablePathDrawing: () => void;
  cancelCablePathDrawing: () => void;
  // Context menu actions for polyline drawing
  showDrawingContextMenu: (
    screenPos: { x: number; y: number },
    floorPlanPoint: { x: number; y: number },
  ) => void;
  hideDrawingContextMenu: () => void;
  // Layer visibility
  toggleCablePathVisibility: () => void;
  setCablePathVisibility: (visible: boolean) => void;
  // Cable Junction Placement Actions
  startCableJunctionPlacement: () => void;
  stopCableJunctionPlacement: () => void;

  // Load Lines Actions
  toggleLoadLineVisibility: () => void;
  setLoadLineVisibility: (visible: boolean) => void;
  setLoadLineThickness: (thickness: number) => void;

  // Utility Actions
  getSelectionCount: (type: EntityType) => number;
  getSelectedIds: (type: EntityType) => string[];
}

type AppStore = AppState & AppActions;

const initialState: AppState = {
  ui: {
    activeDrawer: null,
    previousDrawer: null,
    viewMode: 'fixtures',
    presentationMode: false,
    presentationControlId: null, // 🆕 Initialize presentation state
    presentationLVProductIds: [], // 🆕 Initialize presentation state
    isLoading: false,
    hoveredEntityId: null,
    hoveredEntityType: null,
    showOnlyUnassigned: false,
    showOnlyUnassignedControls: false,
    isEditingFloorPlanImage: false,
    draggedItem: null,
    toolbar: {
      activeTool: 'select',
      isAIMenuOpen: false,
      showOnlyUnassigned: false,
      showOnlyUnassignedControls: false,
      activeMode: 'default',
    },
  },
  selection: {
    fixtures: new Set(),
    circuits: new Set(),
    controls: new Set(),
    rooms: new Set(),
    panels: new Set(),
    products: new Set(),
    annotations: new Set(),
    cablePaths: new Set(),
    cableJunctions: new Set(),
  },
  floorPlan: {
    currentId: null,
    viewport: { scale: 1, positionX: 0, positionY: 0 },
    imageSize: { width: 0, height: 0 },
    isDrawingMode: false,
    isMeasuringMode: false,
  },
  aiTools: {
    confidenceThreshold: 0.3,
    markerOpacity: 0.7,
    isProcessing: false,
    roomConfidenceThreshold: 0.5,
    roomOpacity: 0.7,
    isProcessingRooms: false,
    detectionStride: 448,
  },
  rooms: {
    roomsVisible: false,
    roomOpacity: 0.7,
    confidenceThreshold: 0.5,
    showDimensions: false,
    showArea: true,
    wallsVisible: false,
  },
  products: {
    showLabels: true,
    iconStyle: 'minimalist' as 'minimalist' | 'high-contrast',
  },
  annotations: {
    activeAnnotationType: null,
    isAnnotating: false,
    isEditingText: false,
    currentStyle: {
      color: '#000000',
      strokeWidth: 1,
      fontSize: 16,
      fontFamily: 'Arial',
      opacity: 1,
    },
    showAnnotations: true,
  },
  elevations: {
    isWallSelectionMode: false,
    selectedWallRoomId: null,
    selectedWallIndex: null,
    activeElevationId: null,
    elevationViewport: {
      scale: 1,
      positionX: 0,
      positionY: 0,
    },
  },
  alignment: {
    snapEnabled: true,
    snapThreshold: 10,
    showGuides: true,
  },
  cablePaths: {
    isDrawing: false,
    currentPoints: [],
    startJunctionId: null,
    snapToJunctionId: null,
    liveLength: 0,
    showContextMenu: false,
    contextMenuScreenPosition: null,
    lastClickedPoint: null,
    visible: true,
  },
  loadLines: {
    visible: false,
    thickness: 2,
  },
};

// Create the store with persistence for AI tools settings
export const useAppStore = create<AppStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        immer((set, get) => ({
          ...initialState,

          // UI Actions
          setActiveDrawer: drawer =>
            set(state => {
              // Prevent opening other drawers when in circuit mode
              if (
                state.ui.toolbar.activeMode === 'circuit' &&
                drawer !== null &&
                drawer !== 'circuit'
              ) {
                logger.log('Preventing drawer open - circuit mode is active');
                return;
              }
              // Track the previous drawer before setting the new one
              if (drawer !== state.ui.activeDrawer) {
                state.ui.previousDrawer = state.ui.activeDrawer;
              }
              state.ui.activeDrawer = drawer;

              // Auto-disable active tools when element drawers open
              // This improves UX by automatically returning to select mode when clicking elements
              const ELEMENT_DRAWERS: DrawerType[] = [
                'fixture',
                'control',
                'product',
                'room',
                'circuit',
                'panel',
                'selection',
                'cablePath',
                'cableJunction',
              ];

              if (drawer && ELEMENT_DRAWERS.includes(drawer)) {
                // Reset to default state
                state.ui.toolbar.activeTool = 'select';
                state.ui.toolbar.activeMode = 'default';
                state.annotations.activeAnnotationType = null;
                state.annotations.isAnnotating = false;
              }

              // Clear presentation selections when switching away from presentation drawer
              if (drawer !== 'presentation') {
                state.ui.presentationControlId = null;
                state.ui.presentationLVProductIds = [];
              }
            }),

          setViewMode: mode =>
            set(state => {
              state.ui.viewMode = mode;
            }),

          setPresentationMode: enabled =>
            set(state => {
              state.ui.presentationMode = enabled;
            }),

          setPresentationControlId: id =>
            set(state => {
              state.ui.presentationControlId = id;
            }),

          setPresentationLVProductIds: ids =>
            set(state => {
              state.ui.presentationLVProductIds = ids;
            }),

          setLoading: loading =>
            set(state => {
              state.ui.isLoading = loading;
            }),

          setHoveredEntity: (id, type) =>
            set(state => {
              state.ui.hoveredEntityId = id;
              state.ui.hoveredEntityType = type;
            }),

          setShowOnlyUnassigned: show =>
            set(state => {
              state.ui.showOnlyUnassigned = show;
            }),

          setShowOnlyUnassignedControls: show =>
            set(state => {
              state.ui.showOnlyUnassignedControls = show;
            }),
          setSettingsTab: tab =>
            set(state => {
              state.ui.settingsTab = tab;
            }),
          setEditingFloorPlanImage: editing =>
            set(state => {
              state.ui.isEditingFloorPlanImage = editing;
            }),

          setDraggedItem: item =>
            set(state => {
              state.ui.draggedItem = item;
            }),

          // Toolbar Actions
          setActiveTool: tool =>
            set(state => {
              state.ui.toolbar.activeTool = tool;
              // Reset drawing/measuring modes when changing tools
              if (tool !== 'draw') state.floorPlan.isDrawingMode = false;
              if (tool !== 'measure') state.floorPlan.isMeasuringMode = false;
            }),

          toggleAIMenu: () =>
            set(state => {
              state.ui.toolbar.isAIMenuOpen = !state.ui.toolbar.isAIMenuOpen;
            }),

          setFilter: (filter, value) =>
            set(state => {
              state.ui.toolbar[filter] = value;
            }),

          resetToolbar: () =>
            set(state => {
              state.ui.toolbar = {
                activeTool: 'select',
                isAIMenuOpen: false,
                showOnlyUnassigned: false,
                showOnlyUnassignedControls: false,
                activeMode: 'default',
              };
            }),

          setActiveMode: mode =>
            set(state => {
              state.ui.toolbar.activeMode = mode;
            }),

          setSpacePressed: pressed =>
            set(state => {
              state.ui.toolbar.isSpacePressed = pressed;
            }),

          // Selection Actions
          selectEntity: (type, id) =>
            set(state => {
              // Clear all selections first (including cablePaths for consistency)
              state.selection.fixtures.clear();
              state.selection.circuits.clear();
              state.selection.controls.clear();
              state.selection.rooms.clear();
              state.selection.panels.clear();
              state.selection.products.clear();
              state.selection.annotations.clear();
              state.selection.cablePaths.clear();
              state.selection.cableJunctions.clear();

              // Then set the new selection
              state.selection[`${type}s` as keyof SelectionState] = new Set([id]);
            }),

          toggleEntitySelection: (type, id) =>
            set(state => {
              const key = `${type}s` as keyof SelectionState;
              const selectionSet = state.selection[key];
              const newSet = new Set(selectionSet);
              if (newSet.has(id)) {
                newSet.delete(id);
              } else {
                newSet.add(id);
              }
              state.selection[key] = newSet;
            }),

          selectMultipleEntities: (type, ids) =>
            set(state => {
              state.selection[`${type}s` as keyof SelectionState] = new Set(ids);
            }),

          clearEntitySelection: type =>
            set(state => {
              state.selection[`${type}s` as keyof SelectionState] = new Set();
            }),

          clearAllSelections: () =>
            set(state => {
              state.selection = {
                fixtures: new Set(),
                circuits: new Set(),
                controls: new Set(),
                rooms: new Set(),
                panels: new Set(),
                products: new Set(),
                annotations: new Set(),
                cablePaths: new Set(),
                cableJunctions: new Set(),
              };
            }),

          isEntitySelected: (type, id) => {
            const state = get();
            return state.selection[`${type}s` as keyof SelectionState].has(id);
          },

          // Floor Plan Actions
          setCurrentFloorPlan: id =>
            set(state => {
              state.floorPlan.currentId = id;
            }),

          updateViewport: viewport =>
            set(state => {
              // 🔍 DIAGNOSTIC: Log every viewport update to track coordinate mismatches
              const before = { ...state.floorPlan.viewport };
              Object.assign(state.floorPlan.viewport, viewport);
              const after = { ...state.floorPlan.viewport };

              // Only log if values actually changed (avoid spam from unchanged updates)
              const changed =
                Math.abs((before.scale || 1) - (after.scale || 1)) > 0.001 ||
                Math.abs((before.positionX || 0) - (after.positionX || 0)) > 0.1 ||
                Math.abs((before.positionY || 0) - (after.positionY || 0)) > 0.1 ||
                before.isAnimating !== after.isAnimating;

              if (changed) {
                logger.log('[updateViewport] 📊 VIEWPORT STATE CHANGE:', {
                  before: {
                    scale: before.scale?.toFixed(4) || '1.0000',
                    positionX: before.positionX?.toFixed(2) || '0.00',
                    positionY: before.positionY?.toFixed(2) || '0.00',
                    isAnimating: before.isAnimating || false,
                  },
                  update: viewport,
                  after: {
                    scale: after.scale.toFixed(4),
                    positionX: after.positionX.toFixed(2),
                    positionY: after.positionY.toFixed(2),
                    isAnimating: after.isAnimating || false,
                  },
                  caller: new Error().stack?.split('\n')[2]?.trim() || 'unknown',
                });
              }
            }),

          resetViewport: () =>
            set(state => {
              state.floorPlan.viewport = { scale: 1, positionX: 0, positionY: 0 };
            }),

          setImageSize: size =>
            set(state => {
              state.floorPlan.imageSize = size;
            }),

          setDrawingMode: enabled =>
            set(state => {
              state.floorPlan.isDrawingMode = enabled;
            }),

          setMeasuringMode: enabled =>
            set(state => {
              state.floorPlan.isMeasuringMode = enabled;
            }),

          // AI Tools Actions
          setConfidenceThreshold: threshold =>
            set(state => {
              state.aiTools.confidenceThreshold = threshold;
            }),

          setMarkerOpacity: opacity =>
            set(state => {
              state.aiTools.markerOpacity = opacity;
            }),

          setIsProcessing: processing =>
            set(state => {
              state.aiTools.isProcessing = processing;
            }),

          setRoomConfidenceThreshold: threshold =>
            set(state => {
              state.aiTools.roomConfidenceThreshold = threshold;
            }),

          setRoomOpacity: opacity =>
            set(state => {
              state.aiTools.roomOpacity = opacity;
            }),

          setIsProcessingRooms: processing =>
            set(state => {
              state.aiTools.isProcessingRooms = processing;
            }),

          setDetectionStride: stride =>
            set(state => {
              state.aiTools.detectionStride = stride;
            }),

          // Room Actions
          toggleRoomVisibility: () =>
            set(state => {
              state.rooms.roomsVisible = !state.rooms.roomsVisible;
            }),

          setRoomVisibility: visible =>
            set(state => {
              state.rooms.roomsVisible = visible;
            }),

          setRoomDimensionsVisible: visible =>
            set(state => {
              state.rooms.showDimensions = visible;
            }),

          setRoomAreaVisible: visible =>
            set(state => {
              state.rooms.showArea = visible;
            }),

          toggleRoomDimensions: () =>
            set(state => {
              state.rooms.showDimensions = !state.rooms.showDimensions;
            }),

          toggleWallVisibility: () =>
            set(state => {
              state.rooms.wallsVisible = !state.rooms.wallsVisible;
            }),

          setWallVisibility: visible =>
            set(state => {
              state.rooms.wallsVisible = visible;
            }),

          // Product Actions
          setProductLabelsVisible: visible =>
            set(state => {
              state.products.showLabels = visible;
            }),

          setProductIconStyle: style =>
            set(state => {
              state.products.iconStyle = style;
            }),

          // Annotation Actions
          setActiveAnnotationType: type =>
            set(state => {
              state.annotations.activeAnnotationType = type;
              state.annotations.isAnnotating = type !== null;
              // Update active mode when annotation type changes
              if (type !== null) {
                state.ui.toolbar.activeMode = 'annotation';
                state.ui.toolbar.activeTool = 'annotate';
              } else {
                state.ui.toolbar.activeMode = 'default';
                state.ui.toolbar.activeTool = 'select';
              }
            }),

          setIsAnnotating: isAnnotating =>
            set(state => {
              state.annotations.isAnnotating = isAnnotating;
            }),

          setIsEditingText: isEditing =>
            set(state => {
              state.annotations.isEditingText = isEditing;
            }),

          updateAnnotationStyle: style =>
            set(state => {
              Object.assign(state.annotations.currentStyle, style);
            }),

          toggleAnnotationVisibility: () =>
            set(state => {
              state.annotations.showAnnotations = !state.annotations.showAnnotations;
            }),

          setAnnotationVisibility: visible =>
            set(state => {
              state.annotations.showAnnotations = visible;
            }),

          // Elevation Actions
          setWallSelectionMode: enabled =>
            set(state => {
              state.elevations.isWallSelectionMode = enabled;
              // Clear selection when disabling
              if (!enabled) {
                state.elevations.selectedWallRoomId = null;
                state.elevations.selectedWallIndex = null;
              }
            }),

          setSelectedWall: (roomId, wallIndex) =>
            set(state => {
              state.elevations.selectedWallRoomId = roomId;
              state.elevations.selectedWallIndex = wallIndex;
            }),

          setActiveElevation: elevationId =>
            set(state => {
              state.elevations.activeElevationId = elevationId;
            }),

          updateElevationViewport: viewport =>
            set(state => {
              Object.assign(state.elevations.elevationViewport, viewport);
            }),

          resetElevationViewport: () =>
            set(state => {
              state.elevations.elevationViewport = {
                scale: 1,
                positionX: 0,
                positionY: 0,
              };
            }),

          // Alignment Actions
          toggleSnapEnabled: () =>
            set(state => {
              state.alignment.snapEnabled = !state.alignment.snapEnabled;
            }),

          setSnapThreshold: threshold =>
            set(state => {
              state.alignment.snapThreshold = threshold;
            }),

          toggleShowGuides: () =>
            set(state => {
              state.alignment.showGuides = !state.alignment.showGuides;
            }),

          // Cable Path Actions
          setCablePathDrawing: drawingState =>
            set(state => {
              Object.assign(state.cablePaths, drawingState);
            }),

          startCablePathDrawing: (startJunctionId = null) =>
            set(state => {
              state.cablePaths.isDrawing = true;
              state.cablePaths.currentPoints = [];
              state.cablePaths.startJunctionId = startJunctionId;
              state.cablePaths.snapToJunctionId = null;
              state.cablePaths.liveLength = 0;
              state.cablePaths.showContextMenu = false;
              state.cablePaths.contextMenuScreenPosition = null;
              state.cablePaths.lastClickedPoint = null;
              state.ui.toolbar.activeMode = 'cable-path-drawing';
            }),

          addCablePathPoint: point =>
            set(state => {
              state.cablePaths.currentPoints.push(point);
            }),

          updateCablePathLiveLength: length =>
            set(state => {
              state.cablePaths.liveLength = length;
            }),

          setSnapToJunction: junctionId =>
            set(state => {
              state.cablePaths.snapToJunctionId = junctionId;
            }),

          finishCablePathDrawing: () =>
            set(state => {
              state.cablePaths.isDrawing = false;
              state.cablePaths.showContextMenu = false;
              state.cablePaths.contextMenuScreenPosition = null;
              state.cablePaths.lastClickedPoint = null;
              state.ui.toolbar.activeMode = 'default';
            }),

          cancelCablePathDrawing: () =>
            set(state => {
              state.cablePaths.isDrawing = false;
              state.cablePaths.currentPoints = [];
              state.cablePaths.startJunctionId = null;
              state.cablePaths.snapToJunctionId = null;
              state.cablePaths.liveLength = 0;
              state.cablePaths.showContextMenu = false;
              state.cablePaths.contextMenuScreenPosition = null;
              state.cablePaths.lastClickedPoint = null;
              state.ui.toolbar.activeMode = 'default';
            }),

          showDrawingContextMenu: (screenPos, floorPlanPoint) =>
            set(state => {
              state.cablePaths.showContextMenu = true;
              state.cablePaths.contextMenuScreenPosition = screenPos;
              state.cablePaths.lastClickedPoint = floorPlanPoint;
            }),

          hideDrawingContextMenu: () =>
            set(state => {
              state.cablePaths.showContextMenu = false;
              state.cablePaths.contextMenuScreenPosition = null;
              // Keep lastClickedPoint for reference if needed
            }),

          toggleCablePathVisibility: () =>
            set(state => {
              state.cablePaths.visible = !state.cablePaths.visible;
            }),

          setCablePathVisibility: visible =>
            set(state => {
              state.cablePaths.visible = visible;
            }),

          // Load Lines Actions
          toggleLoadLineVisibility: () =>
            set(state => {
              state.loadLines.visible = !state.loadLines.visible;
            }),

          setLoadLineVisibility: visible =>
            set(state => {
              state.loadLines.visible = visible;
            }),

          setLoadLineThickness: thickness =>
            set(state => {
              state.loadLines.thickness = thickness;
            }),

          startCableJunctionPlacement: () =>
            set(state => {
              state.ui.toolbar.activeMode = 'cable-junction-placement';
            }),

          stopCableJunctionPlacement: () =>
            set(state => {
              state.ui.toolbar.activeMode = 'default';
            }),

          // Utility Actions
          getSelectionCount: type => {
            const state = get();
            return state.selection[`${type}s` as keyof SelectionState].size;
          },

          getSelectedIds: type => {
            const state = get();
            return Array.from(state.selection[`${type}s` as keyof SelectionState]);
          },
        })),
        {
          name: 'floor-plan-ai-tools', // Storage key
          version: 2,
          partialize: state => ({
            // Persist AI tools settings and product label visibility
            aiTools: state.aiTools,
            // Extract clean primitive values to avoid Immer proxy contamination
            products: {
              showLabels: state.products.showLabels,
              iconStyle: state.products.iconStyle,
            },
            loadLines: {
              visible: state.loadLines.visible,
              thickness: state.loadLines.thickness,
            },
          }),
          migrate: (persistedState, version) => {
            // Clear corrupted data from version 1 (Immer proxy contamination)
            if (version < 2) {
              return {} as any; // Return empty to use initial state
            }
            return persistedState as any;
          },
        },
      ),
    ),
  ),
);

// Selector hooks for better performance
export const useUIState = () => useAppStore(state => state.ui);
export const useSelectionState = () => useAppStore(state => state.selection);
export const useFloorPlanState = () => useAppStore(state => state.floorPlan);
export const useAIToolsState = () => useAppStore(state => state.aiTools);
export const useAnnotationState = () => useAppStore(state => state.annotations);
export const useRoomState = () => useAppStore(state => state.rooms);

// Specific selectors
export const useActiveDrawer = () => useAppStore(state => state.ui.activeDrawer);
export const useViewMode = () => useAppStore(state => state.ui.viewMode);
export const useSelectedFixtures = () => useAppStore(state => state.selection.fixtures);
export const useSelectedCircuits = () => useAppStore(state => state.selection.circuits);
export const useSelectedAnnotations = () => useAppStore(state => state.selection.annotations);
export const useViewport = () => useAppStore(state => state.floorPlan.viewport);
export const useActiveAnnotationType = () =>
  useAppStore(state => state.annotations.activeAnnotationType);
export const useAnnotationStyle = () => useAppStore(state => state.annotations.currentStyle);

// Action selectors for common patterns
export const useViewModeActions = () => {
  const viewMode = useAppStore(state => state.ui.viewMode);
  const setViewMode = useAppStore(state => state.setViewMode);
  return { viewMode, setViewMode };
};

// Toolbar-specific selectors
export const useActiveTool = () => useAppStore(state => state.ui.toolbar.activeTool);
export const useToolbarFilters = () =>
  useAppStore(state => ({
    showOnlyUnassigned: state.ui.toolbar.showOnlyUnassigned,
    showOnlyUnassignedControls: state.ui.toolbar.showOnlyUnassignedControls,
  }));

// Room-specific selectors
export const useRoomVisibility = () => useAppStore(state => state.rooms.roomsVisible);
export const useSelectedRooms = () => useAppStore(state => state.selection.rooms);

// Product-specific selectors
export const useProductState = () => useAppStore(state => state.products);
export const useProductLabelsVisible = () => useAppStore(state => state.products.showLabels);
export const useProductIconStyle = () => useAppStore(state => state.products.iconStyle);

// Elevation-specific selectors
export const useElevationState = () => useAppStore(state => state.elevations);
export const useIsWallSelectionMode = () =>
  useAppStore(state => state.elevations.isWallSelectionMode);
export const useActiveElevationId = () => useAppStore(state => state.elevations.activeElevationId);

// Cable Path selectors
export const useCablePathDrawingState = () => useAppStore(state => state.cablePaths);
export const useIsCablePathDrawing = () => useAppStore(state => state.cablePaths.isDrawing);
export const useCablePathCurrentPoints = () => useAppStore(state => state.cablePaths.currentPoints);
export const useSelectedCablePaths = () => useAppStore(state => state.selection.cablePaths);
export const useSelectedCableJunctions = () => useAppStore(state => state.selection.cableJunctions);
export const useCablePathVisibility = () => useAppStore(state => state.cablePaths.visible);

// Load Lines selectors
export const useLoadLineVisibility = () => useAppStore(state => state.loadLines.visible);
export const useLoadLineThickness = () => useAppStore(state => state.loadLines.thickness);
